import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Scale,
  Mail,
  FileText,
  Shield,
  CheckCircle,
  ArrowRight,
  Users,
  Menu,
  X,
  Star,
  Zap,
  Award,
  Sparkles,
  Cpu,
  Layers,
} from 'lucide-react';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const LandingPage: React.FC = () => {
  const [activeTier, setActiveTier] = React.useState<'STANDARD' | 'PRO' | 'UNLIMITED'>('STANDARD');
  const [billingCycle, setBillingCycle] = React.useState<'MONTHLY' | 'YEARLY'>('YEARLY');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const heroCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrame = useRef<number>();

  const heroBadges = useMemo(
    () => ['KVKK Uyumlu Altyapı', 'Avukatlara Özel Akış', '7/24 Danışman Destek'],
    []
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!window.matchMedia) {
        window.matchMedia = (() => {
          const listeners: Array<(e: MediaQueryListEvent) => void> = [];
          const mql: MediaQueryList = {
            matches: false,
            media: '',
            onchange: null,
            addListener: (cb: (ev: MediaQueryListEvent) => void) => listeners.push(cb),
            removeListener: (cb: (ev: MediaQueryListEvent) => void) => {
              const idx = listeners.indexOf(cb);
              if (idx > -1) listeners.splice(idx, 1);
            },
            addEventListener: () => { },
            removeEventListener: () => { },
            dispatchEvent: () => false,
          };
          return () => mql;
        })();
      }
      gsap.registerPlugin(ScrollTrigger);
    }
  }, []);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !(window as any).WebGLRenderingContext ||
      !heroCanvasRef.current
    ) {
      return;
    }

    const renderer = new THREE.WebGLRenderer({
      canvas: heroCanvasRef.current,
      antialias: true,
      alpha: true,
    });
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const uniforms = {
      uTime: { value: 0 },
    };

    const geometry = new THREE.PlaneGeometry(2, 2);
    const material = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        varying vec2 vUv;
        uniform float uTime;

        float dither(vec2 p) {
          return fract(sin(dot(p, vec2(12.9898,78.233))) * 43758.5453);
        }

        void main() {
          vec2 uv = vUv;
          float noise = dither(uv * 128.0 + uTime * 0.01);

          float wave = sin((uv.x + uTime * 0.08) * 8.0) * 0.08;
          float pulse = smoothstep(0.0, 1.0, sin(uTime * 0.4) * 0.5 + 0.5);

          vec3 neonBlue = vec3(0.2, 0.6, 1.0);
          vec3 neonPurple = vec3(0.7, 0.3, 1.0);
          vec3 gradient = mix(neonPurple, neonBlue, uv.y + wave);

          float grid = smoothstep(0.48, 0.5, abs(fract(uv * 6.0 + pulse) - 0.5));
          grid *= (noise * 0.4 + 0.6);

          vec3 color = gradient + vec3(grid * 0.4);
          color += noise * 0.08;

          gl_FragColor = vec4(color, 1.0);
        }
      `,
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const resize = () => {
      if (!heroCanvasRef.current) return;
      const { clientWidth, clientHeight } = heroCanvasRef.current;
      renderer.setSize(clientWidth, clientHeight, false);
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    };

    const renderScene = (time: number) => {
      uniforms.uTime.value = time * 0.001;
      renderer.render(scene, camera);
      animationFrame.current = requestAnimationFrame(renderScene);
    };

    resize();
    window.addEventListener('resize', resize);
    renderScene(0);

    return () => {
      cancelAnimationFrame(animationFrame.current || 0);
      window.removeEventListener('resize', resize);
      renderer.dispose();
      geometry.dispose();
      material.dispose();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    gsap.fromTo(
      '.hero-chip',
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.6, stagger: 0.2, ease: 'power3.out' }
    );

    gsap.utils.toArray<HTMLElement>('.parallax-card').forEach((card) => {
      gsap.fromTo(
        card,
        { y: 60, opacity: 0 },
        {
          scrollTrigger: { trigger: card, start: 'top 80%' },
          y: 0,
          opacity: 1,
          duration: 1.2,
          ease: 'expo.out',
        }
      );
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-30 backdrop-blur-lg bg-slate-950/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Scale className="h-8 w-8 text-cyan-300" />
              <span className="ml-2 text-2xl font-bold tracking-tight text-white">
                Avukasa
              </span>
            </div>
            <nav className="hidden md:flex space-x-8 text-sm font-medium text-slate-300">
              <a href="#features" className="hover:text-white transition-colors">
                Özellikler
              </a>
              <a href="#atlas" className="hover:text-white transition-colors">
                Atlas
              </a>
              <a href="#pricing" className="hover:text-white transition-colors">
                Planlar
              </a>
            </nav>
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/login" className="text-slate-200 hover:text-white transition-colors">
                Giriş Yap
              </Link>
              <Link
                to="/signup"
                className="bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-900 font-semibold px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
              >
                Başlayın
              </Link>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden border-t border-white/10 py-4 space-y-4">
              <a href="#features" className="block text-white">
                Özellikler
              </a>
              <a href="#atlas" className="block text-white">
                Atlas
              </a>
              <a href="#pricing" className="block text-white">
                Planlar
              </a>
              <div className="flex flex-col space-y-2">
                <Link to="/login" className="text-slate-200 hover:text-white">
                  Giriş Yap
                </Link>
                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-900 font-semibold px-4 py-2 rounded-full text-center"
                >
                  Başlayın
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>

      <section className="relative overflow-hidden">
        <canvas
          ref={heroCanvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/40 to-slate-950" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid gap-12 lg:grid-cols-2">
          <div className="space-y-8">
            <p className="uppercase tracking-[0.3em] text-xs text-slate-300">
              HUKUK OFİSİ PLATFORMU
            </p>
            <h1 className="text-4xl md:text-6xl font-semibold leading-tight text-white">
              Ofisinizi Dijital Asistanla
              <span className="text-cyan-300 block">Güvenle Yönetin</span>
            </h1>
            <p className="text-lg text-slate-300 max-w-xl">
              Avukasa; e-postalarınızı, dava dosyalarınızı ve müvekkil iletişimlerinizi tek ekranda
              toplar. Türkçe arayüz, KVKK uyumlu altyapı ve hukuk ofislerinin beklentilerine göre
              tasarlanmış iş akışlarıyla zamandan tasarruf edin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/signup"
                className="bg-white text-slate-900 px-8 py-4 rounded-full text-lg font-semibold flex items-center justify-center hover:bg-slate-100 transition-colors"
              >
                Hemen Başlayın
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <button className="border border-white/20 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition-colors">
                Tanıtım Talep Et
              </button>
            </div>
            <div className="flex flex-wrap gap-3 pt-4">
              {heroBadges.map((badge) => (
                <span
                  key={badge}
                  className="hero-chip inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs uppercase tracking-widest text-slate-200"
                >
                  <Sparkles className="h-4 w-4 text-cyan-300" />
                  {badge}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-r from-cyan-500/30 to-purple-500/30 blur-3xl opacity-40" />
            <div className="relative bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-3xl shadow-2xl space-y-8">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-cyan-200">
                <span>Ofis Nabzı</span>
                <span>Anlık Özet</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Hazırlanan Doküman', value: '3.2K', icon: FileText },
                  { label: 'İşlenen E-posta', value: '12.4K', icon: Mail },
                  { label: 'Güven Puanı', value: '99.7%', icon: Shield },
                  { label: 'Aktif Müvekkil', value: '800+', icon: Users },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="parallax-card bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3"
                  >
                    <item.icon className="h-8 w-8 text-cyan-300" />
                    <div>
                      <p className="text-xs text-slate-400 uppercase tracking-wide">{item.label}</p>
                      <p className="text-2xl font-semibold text-white">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 border border-white/5">
                <p className="text-sm uppercase text-slate-400 mb-2 tracking-[0.2em]">
                  Avukat Görüşü
                </p>
                <p className="text-slate-100 leading-relaxed">
                  “Müvekkil iletişimlerim, mahkeme yazışmalarım ve ekip notları tek ekranda. Ofisteki herkes aynı dosyanın son durumunu anında görüyor.”
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          {[
            {
              icon: Cpu,
              title: 'Güvenli Altyapı',
              text:
                'Türkiye lokasyonlu sunucular, şifreli yedekleme ve erişim kayıtlarıyla tüm dosyalarınız güven altında. KVKK denetimleri için raporları tek tıkla alın.',
            },
            {
              icon: Layers,
              title: 'Esnek İş Akışları',
              text:
                'Her avukat kendi çalışma stiline göre paneller oluşturabilir. Dosya tipleri, e-posta şablonları ve not alanları ofisinizin alışkanlıklarına göre şekillenir.',
            },
          ].map((item) => (
            <div key={item.title} className="parallax-card bg-white/5 border border-white/10 rounded-3xl p-8 space-y-4">
              <item.icon className="h-10 w-10 text-cyan-300" />
              <h3 className="text-2xl font-semibold text-white">{item.title}</h3>
              <p className="text-slate-300 text-sm leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="features" className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="uppercase tracking-[0.3em] text-xs text-cyan-300">Öne Çıkan Noktalar</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Avukatlar İçin Tasarlandı
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              Avukasa'nın her modülü, yoğun dosya trafiği yaşayan hukuk ofislerinin geri bildirimleriyle şekillendi. Rahat, güvenli ve erişilebilir bir iş akışı sunuyoruz.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Mail,
                title: 'Akıllı E-Posta Kutusu',
                desc:
                  'Müvekkil ve mahkeme yazışmalarınız otomatik olarak sınıflandırılır, önemli e-postalar kaçmaz. Tek tıkla dosya ya da etikete ulaşın.',
                bullets: ['Otomatik kategori', 'Bildirim ve hatırlatma', 'Arşivlenmiş yazışmalar'],
                accent: 'from-cyan-500/30 via-slate-900 to-transparent',
              },
              {
                icon: FileText,
                title: 'Hazır Şablon Asistanı',
                desc:
                  'Sık kullanılan dilekçeleri kaydedin, otomatik doldurma ile dakikalar içinde hazır hale getirin. Tüm ekip aynı dilde ilerler.',
                bullets: ['Word/PDF uyumu', 'Toplu belge üretimi', 'Hata uyarıları'],
                accent: 'from-purple-500/30 via-slate-900 to-transparent',
              },
              {
                icon: Shield,
                title: 'Güvenli Arşiv',
                desc:
                  'Belgeleriniz Türkiye lokasyonlu sunucularda korunur. Kim hangi dosyayı görüntüledi, kime paylaşıldı hepsi kayıt altında.',
                bullets: ['KVKK uyumluluğu', 'Yetki yönetimi', 'Şifreli yedek'],
                accent: 'from-blue-500/30 via-slate-900 to-transparent',
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="parallax-card relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-2xl"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.accent} opacity-40`} />
                <div className="relative space-y-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-cyan-300">
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white">{feature.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{feature.desc}</p>
                  <ul className="space-y-2 text-sm text-slate-200">
                    {feature.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-cyan-300" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="atlas" className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="uppercase tracking-[0.3em] text-xs text-cyan-300">Atlas</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Rakamlarla Avukasa</h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Binlerce avukat güveniyor, milyonlarca doküman işleniyor
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, value: '500+', label: 'Aktif Avukat', detail: 'Her gün büyüyen topluluk' },
              { icon: Zap, value: '75%', label: 'Zaman Tasarrufu', detail: 'Ortalama verimlilik artışı' },
              { icon: Award, value: '10K+', label: 'Aylık Doküman', detail: 'Otomatik belge üretimi' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="text-center bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-xl"
              >
                <div className="bg-white/10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <stat.icon className="h-8 w-8 text-cyan-300" />
                </div>
                <div className="text-5xl font-bold mb-2 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-slate-200 text-lg">{stat.label}</div>
                <div className="text-sm text-slate-400 mt-2">{stat.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Avukatlarımız Ne Diyor?</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Binlerce avukat Avukasa ile çalışma şeklini değiştirdi
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote:
                  '“Avukasa sayesinde döküman hazırlama sürem %80 azaldı. Artık müvekkillerime daha fazla zaman ayırabiliyorum.”',
                name: 'Av. Ahmet Yılmaz',
                field: 'Ticaret Hukuku',
                color: 'bg-blue-100 text-blue-900',
                initials: 'AY',
              },
              {
                quote:
                  '“Email takibi özelliği harika! Hiçbir önemli mesajı kaçırmıyorum. İş akışım çok daha organize oldu.”',
                name: 'Av. Merve Kaya',
                field: 'İş Hukuku',
                color: 'bg-green-100 text-green-900',
                initials: 'MK',
              },
              {
                quote:
                  '“Şablon sistemi mükemmel. Sözleşmelerimi saniyeler içinde hazırlayabiliyorum. Kesinlikle tavsiye ederim.”',
                name: 'Av. Emre Özkan',
                field: 'Gayrimenkul Hukuku',
                color: 'bg-purple-100 text-purple-900',
                initials: 'EÖ',
              },
            ].map((testimonial) => (
              <div key={testimonial.name} className="bg-slate-50 p-6 rounded-3xl">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">{testimonial.quote}</p>
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${testimonial.color}`}>
                    <span className="font-semibold">{testimonial.initials}</span>
                  </div>
                  <div className="ml-3">
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.field}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Basit ve Şeffaf Fiyatlandırma
            </h2>
            <p className="text-lg text-slate-400">
              İhtiyacınıza uygun planı seçin, istediğiniz zaman değiştirin.
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-slate-900 p-1 rounded-full border border-white/10 inline-flex relative items-center">
              <button
                onClick={() => setBillingCycle('MONTHLY')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${billingCycle === 'MONTHLY' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'
                  }`}
              >
                Aylık
              </button>
              <button
                onClick={() => setBillingCycle('YEARLY')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${billingCycle === 'YEARLY' ? 'bg-cyan-500/20 text-cyan-300' : 'text-slate-400 hover:text-white'
                  }`}
              >
                Yıllık
                <span className="text-[10px] font-bold bg-cyan-500 text-slate-900 px-2 py-0.5 rounded-full">
                  %20 İNDİRİM
                </span>
              </button>
            </div>
          </div>

          {/* Tier Toggle Switch */}
          <div className="flex justify-center mb-12">
            <div className="bg-slate-900 p-1 rounded-xl border border-white/10 inline-flex relative w-[360px]">
              <div
                className={`absolute inset-y-1 w-[32%] bg-white/10 rounded-lg transition-all duration-300 ease-out ${activeTier === 'STANDARD' ? 'translate-x-[2%]' :
                  activeTier === 'PRO' ? 'translate-x-[106%]' : 'translate-x-[209%]'
                  }`}
              />
              <button
                onClick={() => setActiveTier('STANDARD')}
                className={`relative z-10 flex-1 py-3 text-sm font-semibold transition-colors duration-300 rounded-lg ${activeTier === 'STANDARD' ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
              >
                Standart
              </button>
              <button
                onClick={() => setActiveTier('PRO')}
                className={`relative z-10 flex-1 py-3 text-sm font-semibold transition-colors duration-300 rounded-lg ${activeTier === 'PRO' ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
              >
                Profesyonel
              </button>
              <button
                onClick={() => setActiveTier('UNLIMITED')}
                className={`relative z-10 flex-1 py-3 text-sm font-semibold transition-colors duration-300 rounded-lg ${activeTier === 'UNLIMITED' ? 'text-white' : 'text-slate-400 hover:text-slate-200'
                  }`}
              >
                Sınırsız
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[
              {
                id: 'mail',
                name: 'E-Posta Modülü',
                price: billingCycle === 'YEARLY'
                  ? (activeTier === 'STANDARD' ? '₺399' : activeTier === 'PRO' ? '₺999' : '₺1.999')
                  : (activeTier === 'STANDARD' ? '₺499' : activeTier === 'PRO' ? '₺1.299' : '₺2.499'),
                description: activeTier === 'STANDARD'
                  ? 'Bireysel avukatlar için temel e-posta takibi.'
                  : activeTier === 'PRO'
                    ? 'Ofisinizi büyütmek için gelişmiş takip.'
                    : 'Büyük ölçekli ofisler için sınırsız yönetim.',
                perks: activeTier === 'STANDARD'
                  ? ['1 E-posta hesabı', '5 Takip etiketi', 'Günlük veri yenileme', 'Temel destek']
                  : activeTier === 'PRO'
                    ? ['5 E-posta hesabı', 'Sınırsız etiket', '15dk senkronizasyon', 'Öncelikli destek', '1 Yıl arşiv']
                    : ['Sınırsız E-posta hesabı', 'Gerçek zamanlı senkronizasyon', 'Sınırsız arşiv', '7/24 Özel Temsilci', 'API Erişimi'],
                highlight: false,
              },
              {
                id: 'template',
                name: 'Şablon Modülü',
                price: billingCycle === 'YEARLY'
                  ? (activeTier === 'STANDARD' ? '₺479' : activeTier === 'PRO' ? '₺1.199' : '₺2.399')
                  : (activeTier === 'STANDARD' ? '₺599' : activeTier === 'PRO' ? '₺1.499' : '₺2.999'),
                description: activeTier === 'STANDARD'
                  ? 'Sık kullanılan dilekçeler için pratik çözüm.'
                  : activeTier === 'PRO'
                    ? 'Ekip işbirliği ve word formatı desteği.'
                    : 'Tam otomasyon ve özel entegrasyonlar.',
                perks: activeTier === 'STANDARD'
                  ? ['3 Aktif şablon', 'Aylık 20 döküman', 'Sadece PDF çıktı', 'Temel değişkenler']
                  : activeTier === 'PRO'
                    ? ['20 Aktif şablon', 'Aylık 200 döküman', 'Word (.docx) + PDF', 'Gelişmiş mantık', 'Ekip paylaşımı']
                    : ['Sınırsız şablon', 'Sınırsız üretim', ' Tüm formatlar', 'API ile üretim', 'Özel değişkenler'],
                highlight: false,
              },
              {
                id: 'bundle',
                name: 'Avukasa Ofis',
                price: billingCycle === 'YEARLY'
                  ? (activeTier === 'STANDARD' ? '₺699' : activeTier === 'PRO' ? '₺1.999' : '₺3.999')
                  : (activeTier === 'STANDARD' ? '₺899' : activeTier === 'PRO' ? '₺2.499' : '₺4.999'),
                description: activeTier === 'STANDARD'
                  ? 'Küçük ofisler için ekonomik başlangıç paketi.'
                  : activeTier === 'PRO'
                    ? 'Orta ölçekli bürolar için ideal çözüm.'
                    : 'Kurumsal hukuk departmanları için tam güç.',
                perks: activeTier === 'STANDARD'
                  ? ['1 Kullanıcı', 'Standart Modüller', 'Mobil Uygulama', 'E-posta Desteği']
                  : activeTier === 'PRO'
                    ? ['3 Kullanıcı', 'Pro Modüller', '50 GB Depolama', 'Öncelikli Destek', 'Personel Yetki Yönetimi']
                    : ['Sınırsız Kullanıcı', 'Sınırsız Modüller', '1 TB Depolama', 'Özel Müşteri Temsilcisi', 'Yerinde Kurulum & Eğitim'],
                highlight: true,
              },
            ].map((plan) => (
              <div
                key={plan.name}
                className={`rounded-3xl p-8 border flex flex-col transition-all duration-300 ${plan.highlight
                  ? activeTier === 'UNLIMITED'
                    ? 'bg-gradient-to-b from-purple-800 to-slate-900 border-purple-500 text-white shadow-2xl relative overflow-hidden transform hover:-translate-y-2 ring-1 ring-purple-400'
                    : 'bg-gradient-to-b from-blue-600 to-blue-900 border-blue-400 text-white shadow-2xl relative overflow-hidden transform hover:-translate-y-2'
                  : 'bg-white/5 border-white/10 text-slate-100 hover:bg-white/10'
                  }`}
              >
                {plan.highlight && (
                  <>
                    <div className={`absolute top-0 right-0 text-slate-900 text-xs font-bold px-3 py-1 rounded-bl-lg ${activeTier === 'UNLIMITED' ? 'bg-purple-300' : 'bg-yellow-400'}`}>
                      {activeTier === 'UNLIMITED' ? 'KURUMSAL GÜÇ' : activeTier === 'PRO' ? 'EN GÜÇLÜ' : 'EN POPÜLER'}
                    </div>
                  </>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-sm mb-6 text-slate-300 min-h-[48px] items-center flex">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold tracking-tight">{plan.price}</span>
                  <span className="text-sm ml-2 opacity-70">/ay</span>
                </div>
                <div className="flex-grow">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-4 font-semibold">
                    {activeTier === 'STANDARD' ? 'STANDART ÖZELLİKLER' : activeTier === 'PRO' ? 'PRO AVANTAJLARI' : 'SINIRSIZ GÜÇ'}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {plan.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-3 text-sm">
                        <CheckCircle className={`h-5 w-5 flex-shrink-0 ${plan.highlight ? activeTier === 'UNLIMITED' ? 'text-purple-300' : 'text-yellow-300' : 'text-cyan-300'}`} />
                        <span className="leading-tight">{perk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/signup"
                  className={`w-full inline-flex justify-center items-center py-4 rounded-xl font-bold text-sm tracking-wide transition-all ${plan.highlight
                    ? activeTier === 'UNLIMITED'
                      ? 'bg-white text-purple-900 hover:bg-slate-100 shadow-lg'
                      : 'bg-white text-blue-900 hover:bg-slate-100 shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                >
                  {activeTier === 'UNLIMITED' ? 'EKİBİNİ TAŞI' : activeTier === 'PRO' ? 'PROFESYONEL BAŞLA' : 'HEMEN BAŞLA'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Hukuki Süreçlerinizi Bugün Otomatikleştirin
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            14 gün ücretsiz deneme. Kredi kartı gerektirmez. İstediğiniz zaman iptal edebilirsiniz.
          </p>
          <Link
            to="/login"
            className="bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-900 px-8 py-4 rounded-full font-semibold inline-flex items-center justify-center hover:opacity-90 transition"
          >
            Ücretsiz Deneme Başlatın
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      <footer className="bg-slate-950 border-t border-white/10 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Scale className="h-6 w-6 text-cyan-300" />
                <span className="ml-2 text-xl font-bold">Avukasa</span>
              </div>
              <p className="text-slate-400">
                Hukuki süreçlerinizi otomatikleştiren güvenli ve kullanıcı dostu platform.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Ürün</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <a href="#features" className="hover:text-white transition-colors">
                    Özellikler
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white transition-colors">
                    Fiyatlar
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Yasal</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <button className="hover:text-white transition-colors text-left">Gizlilik</button>
                </li>
                <li>
                  <button className="hover:text-white transition-colors text-left">Şartlar</button>
                </li>
                <li>
                  <button className="hover:text-white transition-colors text-left">KVKK</button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; 2024 Avukatech. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
